import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRawTutorial } from '.';
import useDebounceCallback from '../useDebounceCallback';

const DEFAULT_STATE = {
  tutorials: [],
  errors: null,
};

const DEFAULT_PARAMS = {};
const DEFAULT_OPTIONS = {};

export const useTutorial = (
  params = DEFAULT_PARAMS,
  options = DEFAULT_OPTIONS,
) => {
  const { t, i18n } = useTranslation();
  const { debounce = 0 } = options;
  const { data: tutorialData, errors: tutorialDataErrors } = useRawTutorial(
    params,
    options,
  );

  const [{ tutorials, errors }, setResponseInfo] = useState(DEFAULT_STATE);

  const reload = useCallback(() => {
    if (tutorialData?.length) {
      const isLangPl = i18n.language === 'pl';
      const validateData = tutorialData
        .sort((a, b) => a?.order - b?.order)
        .map((item) => {
          const isTargetBody =
            item?.target === 'body' || item?.placement === 'center';
          const disableBeacon = item?.disableBeacon || isTargetBody;

          return {
            placement: item.placement,
            title: isLangPl ? item.title_pl : item.title,
            content: isLangPl ? item?.content_pl : item?.content,
            target: item.target,
            data: {
              next: item.next,
              previous: item.previous,
            },
            disableBeacon,
            floaterProps: { hideArrow: true },
            hideCloseButton: disableBeacon,
            page: item.page,
            // Modal tooltip hover description for beacon
            locale: { open: t('Tutorials.OpenTutorial') },
            /**
             * When the target is the body, the beacon’s visibility is hidden, but it still retains its height and width
             * It results in scrolling issues on the overlay, necessitating that the beacon be positioned at the top.
             */
            placementBeacon: isTargetBody ? 'top' : item?.placement,
            testId: item.testId,
          };
        });

      if (validateData.length > 0) {
        setResponseInfo({
          tutorials: validateData,
          errors: null,
        });
      }
    } else if (tutorialDataErrors) {
      setResponseInfo({
        ...DEFAULT_STATE,
        errors: tutorialDataErrors,
      });
    }
  }, [i18n.language, t, tutorialData, tutorialDataErrors]);

  const debouncedReload = useDebounceCallback(reload, debounce);
  useEffect(debouncedReload, [debouncedReload]);

  const result = [tutorials, errors, debouncedReload];
  Object.assign(result, {
    tutorials,
    errors,
    reload: debouncedReload,
  });
  return result;
};
